import React, { useState } from 'react';
import { Lightbulb, Check } from 'lucide-react';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import type { Recommendation } from './RecommendationsModal';
import styles from './TipsAndRecommendations.module.css';

interface TipsAndRecommendationsProps {
  tip: string;
  recommendations: Recommendation[];
  onApplyRecommendations: (selectedIds: string[]) => void;
  isApplying?: boolean;
}

export const TipsAndRecommendations: React.FC<TipsAndRecommendationsProps> = ({
  tip,
  recommendations,
  onApplyRecommendations,
  isApplying = false,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === recommendations.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(recommendations.map((r) => r.id));
    }
  };

  const handleApply = () => {
    if (selectedIds.length === 0) return;
    onApplyRecommendations(selectedIds);
    setSelectedIds([]);
  };

  return (
    <div className={styles.container}>
      {/* ProTip Section — only show when AI generates a pro-tip */}
      {tip ? (
        <div className={styles.tipSection}>
          <Lightbulb size={14} className={styles.tipIcon} />
          <div className={styles.tipText}>
            <strong>ProTip:</strong> {tip}
          </div>
        </div>
      ) : null}

      {/* Recommendations Section */}
      {recommendations.length > 0 && (
        <div className={styles.recommendationsSection}>
          <div className={styles.recommendationsLabel}>
            <span>Suggested improvements:</span>
            <span className={styles.count}>
              {selectedIds.length} of {recommendations.length}
            </span>
          </div>

          <div className={styles.recommendationsList}>
            {recommendations.map((rec) => (
              <label
                key={rec.id}
                className={`${styles.recommendationItem} ${
                  selectedIds.includes(rec.id) ? styles.selected : ''
                }`}
              >
                <Checkbox
                  checked={selectedIds.includes(rec.id)}
                  onChange={() => handleToggle(rec.id)}
                  disabled={isApplying}
                />
                <div className={styles.itemContent}>
                  <span className={styles.itemTitle}>{rec.title}</span>
                  <span className={`${styles.priorityBadge} ${styles[`priority${rec.priority}`]}`}>
                    {rec.priority}
                  </span>
                </div>
              </label>
            ))}
          </div>

          <div className={styles.actions}>
            {selectedIds.length > 0 && (
              <Button
                size="sm"
                onClick={handleSelectAll}
                variant="ghost"
                disabled={isApplying}
                className={styles.clearBtn}
              >
                {selectedIds.length === recommendations.length ? 'Clear All' : 'Select All'}
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleApply}
              disabled={selectedIds.length === 0 || isApplying}
              className={styles.applyBtn}
            >
              {isApplying ? (
                <>
                  <span className={styles.spinner} />
                  Applying...
                </>
              ) : (
                <>
                  <Check size={14} />
                  Apply ({selectedIds.length})
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
